import { TemplateFile } from '@api';

/**
 * Returns the first key in the object that has a truthy value
 */
export function tooltipTitle(titles: { [title: string]: any }) {
    const [ title ] = Object.entries(titles).find(([ _, shouldShow ]) => shouldShow) || [];
    return title || '';
}

/**
 * Converts from camel or pascal case to lower case with spaces, i.e. "camelCase" -> "camel case"
 */
export function toLowerCaseWithSpaces(str: string) {
    return str.replace(/([A-Z])/g, ' $1').toLowerCase();
}

/**
 * Converts from camel or pascal case to sentence case
 */
export function toSentenceCase(str: string) {
    return str.replace(/([A-Z][a-z])/g, ' $1')
        .replace(/\//g, ' /') // add spaces around slashes
        .replace(/^./, (s) => s.toUpperCase());
}

/**
 * Returns a new array with the item at the specified index replaced by the new item
 */
export function replaceItemByIndex<T>(array: T[], newItem: T, index: number) {
    return [
        ...array.slice(0, index),
        newItem,
        ...array.slice(index + 1)
    ];
}

/**
 * Constructs the account base route using the clientId and optional customerId.
 * Returns the route in the format:
 * `/accounts/{clientId}` or
 * `/accounts/{clientId}/{customerId}` if the customer ID is provided.
 */
export function getAccountBaseRoute(clientId: string, customerId?: string) {
    return `/accounts/${clientId}${customerId ? `/${customerId}` : ''}`;
}

export function handleDownload(file: Blob | TemplateFile, fileName: string) {
    if (file instanceof Blob) {
        const url = window.URL.createObjectURL(file);
        const link = document.createElement('a');
        link.href = url;
        link.download = fileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
    } else {
        const link = document.createElement('a');
        link.href = file.fileUrl;
        link.download = fileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    }
}

type ItemWithDisplayOrder = { displayOrder: number, isActive?: boolean, [key: string]: any };

export function getMinDisplayOrder(itemsWithDisplayOrder: ItemWithDisplayOrder[]) {
    return itemsWithDisplayOrder.reduce(
        (min, { displayOrder }) => Math.min(min, displayOrder),
        itemsWithDisplayOrder[0].displayOrder
    );
}

export function getMaxDisplayOrder(itemsWithDisplayOrder: ItemWithDisplayOrder[]) {
    return itemsWithDisplayOrder.reduce(
        (max, { displayOrder }) => Math.max(max, displayOrder),
        itemsWithDisplayOrder[0].displayOrder
    );
}

/**
 * Gets the minimum display order among active items
 */
export function getMinActiveDisplayOrder(itemsWithDisplayOrder: ItemWithDisplayOrder[]) {
    // Filter to only include active items (or those without an isActive property)
    const activeItems = itemsWithDisplayOrder.filter(item => item.isActive !== false);

    if (activeItems.length === 0) {
        return 0; // Return default if no active items
    }

    return activeItems.reduce(
        (min, { displayOrder }) => Math.min(min, displayOrder),
        activeItems[0].displayOrder
    );
}

/**
 * Gets the maximum display order among active items
 */
export function getMaxActiveDisplayOrder(itemsWithDisplayOrder: ItemWithDisplayOrder[]) {
    // Filter to only include active items (or those without an isActive property)
    const activeItems = itemsWithDisplayOrder.filter(item => item.isActive !== false);

    if (activeItems.length === 0) {
        return 0; // Return default if no active items
    }

    return activeItems.reduce(
        (max, { displayOrder }) => Math.max(max, displayOrder),
        activeItems[0].displayOrder
    );
}

/**
 * Gets the next available display order for new items
 */
export function getNextAvailableDisplayOrder(itemsWithDisplayOrder: ItemWithDisplayOrder[]) {
    if (itemsWithDisplayOrder.length === 0) {
        return 1;
    }

    const maxOrder = itemsWithDisplayOrder.reduce(
        (max, { displayOrder }) => Math.max(max, displayOrder || 0),
        0
    );

    return maxOrder + 1;
}

/**
 * Finds the next active item's display order in the specified direction
 */
export function findAdjacentActiveDisplayOrder(
    items: ItemWithDisplayOrder[],
    currentDisplayOrder: number,
    direction: 'up' | 'down'
) {
    // Filter to only include active items
    const activeItems = items.filter(item => item.isActive !== false);

    // Sort items by display order
    const sortedActiveItems = [ ...activeItems ].sort((a, b) => a.displayOrder - b.displayOrder);

    if (direction === 'up') {
        // Find the next lower active display order
        const prevItem = sortedActiveItems.reverse().find(item => item.displayOrder < currentDisplayOrder);
        return prevItem ? prevItem.displayOrder : null;
    } else {
        // Find the next higher active display order
        const nextItem = sortedActiveItems.find(item => item.displayOrder > currentDisplayOrder);
        return nextItem ? nextItem.displayOrder : null;
    }
}
